import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiHeart, FiTool, FiBriefcase, FiStar, FiCheckCircle } = FiIcons;

const SolutionSection = () => {
  const steps = [
    {
      icon: FiHome,
      title: 'Shelter & Safety',
      description: 'Safe accommodation with proper facilities, medical care, and counseling support',
      image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      features: ['24/7 Security', 'Medical Care', 'Counseling', 'Family Reunification']
    },
    {
      icon: FiHeart,
      title: 'Food & Healthcare',
      description: 'Nutritious meals, regular health checkups, and mental health support for complete recovery',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      features: ['3 Nutritious Meals', 'Health Checkups', 'Mental Health Support', 'Addiction Recovery']
    },
    {
      icon: FiTool,
      title: 'Skills Training',
      description: 'Comprehensive training in cooking, farming, crafts, and digital skills for sustainable livelihoods',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      features: ['Cooking & Catering', 'Organic Farming', 'Handicrafts', 'Digital Literacy']
    },
    {
      icon: FiBriefcase,
      title: 'Employment Opportunities',
      description: 'Job placement assistance and support to start their own businesses with dignity',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      features: ['Job Placement', 'Business Support', 'Micro-loans', 'Ongoing Mentorship']
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 left-40 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 right-40 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiStar} className="w-4 h-4" />
            <span>Our Proven Solution</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Not Charity.
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">
              Dignity.
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our 4-step rehabilitation process transforms lives permanently by addressing root causes, not just symptoms.
          </p>
        </motion.div>

        {/* Solution Steps */}
        <div className="space-y-20">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="flex items-center space-x-4 mb-6">
                  <div className={`w-16 h-16 ${step.bgColor} rounded-2xl flex items-center justify-center`}>
                    <SafeIcon icon={step.icon} className={`w-8 h-8 bg-gradient-to-r ${step.color} bg-clip-text text-transparent`} />
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl font-bold text-gray-400">0{index + 1}</div>
                    <div className="w-12 h-0.5 bg-gray-300"></div>
                  </div>
                </div>
                
                <h3 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">{step.description}</p>
                
                {/* Features List */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {step.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Impact Stats */}
                <div className={`bg-gradient-to-r ${step.color} rounded-xl p-6 text-white`}>
                  <div className="text-2xl font-bold mb-1">95% Success Rate</div>
                  <div className="text-sm opacity-90">People who complete this step successfully transition to independence</div>
                </div>
              </div>

              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="relative">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    
                    {/* Overlay Badge */}
                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-3">
                      <div className="text-sm font-semibold text-gray-900">Step {index + 1}</div>
                    </div>
                  </div>

                  {/* Floating Success Card */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 3, delay: index }}
                    className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-2xl border-l-4 border-green-500"
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-500" />
                      <div>
                        <div className="font-bold text-gray-900">Success Story</div>
                        <div className="text-sm text-gray-600">Real transformation</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-orange-500/20 to-transparent rounded-full"></div>
            <div className="relative">
              <h3 className="text-3xl font-bold mb-4">This Process Works</h3>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                2,547 people have already completed this journey. Each donation brings someone one step closer to independence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-400 transition-colors duration-200">
                  💪 Fund This Solution - Donate Now
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-gray-900 transition-colors duration-200">
                  See Success Stories
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;